import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, XIcon, RocketIcon } from 'lucide-react';
import { Logo } from './Logo';
import { Link, useLocation } from 'react-router-dom';
export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const location = useLocation();
  // Close menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setComingSoonOpen(false);
  }, [location.pathname]);
  // Lock body scroll when menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);
  // Lock body scroll when coming soon lightbox is open
  useEffect(() => {
    if (comingSoonOpen) {
      document.body.style.overflow = 'hidden';
    } else if (!mobileMenuOpen) {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [comingSoonOpen, mobileMenuOpen]);
  const navLinks = [{
    label: 'About',
    to: '/about',
    isExternal: false,
    isButton: false
  }, {
    label: 'Request a tool',
    to: 'mailto:tools@4all.ai?subject=Request%20a%20tool%20for%20your%20platform&body=Hey%2C%20I%27d%20like%20to%20submit%20a%20tool!%0A%0ATool%20Name%3A%0A%0ATool%20Link%3A',
    isExternal: true,
    isButton: false
  }, {
    label: 'Become a partner',
    to: '/partner',
    isExternal: false,
    isButton: false
  }, {
    label: 'Submit a tool',
    to: 'https://4alltools.typeform.com/to/nk4pUY40',
    isExternal: true,
    isButton: true
  }];
  return <header ref={headerRef} className="w-full border-b border-grey-200 bg-white sticky top-0 z-50" data-id="element-29">
      <div className="w-full bg-gradient-to-r from-cyan-200 via-purple-200 to-pink-200 py-2 px-4 text-center" data-id="element-30">
        <p className="text-xs sm:text-sm font-medium text-grey-800" data-id="element-31">
          ✨ Discover the best AI tools - new tools every week 🔥
        </p>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between" data-id="element-32">
        <Link to="/" className="shrink-0" data-id="element-33">
          <Logo width={400} height={136} className="invert mix-blend-multiply max-w-[160px] sm:max-w-[200px] md:max-w-none h-auto" data-id="element-34" />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8" data-id="element-35">
          {navLinks.map(link => link.isExternal ? link.isButton ? <a key={link.label} href={link.to} target="_blank" rel="noopener noreferrer" className="rounded-full bg-black text-white px-5 py-2 text-sm font-medium hover:bg-grey-800 transition-colors" data-id="element-36">
                  {link.label}
                </a> : null : <Link key={link.label} to={link.to} className="text-sm text-grey-600 hover:text-black transition-colors" data-id="element-37">
                {link.label}
              </Link>)}
        </nav>

        {/* Mobile Hamburger Button */}
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden flex items-center justify-center w-10 h-10 rounded-full hover:bg-grey-100 transition-colors" aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileMenuOpen} data-id="element-38">
          <AnimatePresence mode="wait" initial={false} data-id="element-39">
            {mobileMenuOpen ? <motion.div key="close" initial={{
            opacity: 0,
            rotate: -90
          }} animate={{
            opacity: 1,
            rotate: 0
          }} exit={{
            opacity: 0,
            rotate: 90
          }} transition={{
            duration: 0.15
          }} data-id="element-40">
                <XIcon className="w-5 h-5 text-black" data-id="element-41" />
              </motion.div> : <motion.div key="menu" initial={{
            opacity: 0,
            rotate: 90
          }} animate={{
            opacity: 1,
            rotate: 0
          }} exit={{
            opacity: 0,
            rotate: -90
          }} transition={{
            duration: 0.15
          }} data-id="element-42">
                <MenuIcon className="w-5 h-5 text-black" data-id="element-43" />
              </motion.div>}
          </AnimatePresence>
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence data-id="element-44">
        {mobileMenuOpen && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 md:hidden" style={{
          top: headerRef.current?.getBoundingClientRect().bottom ?? 0
        }} onClick={() => setMobileMenuOpen(false)} data-id="element-45" />

            {/* Menu Panel */}
            <motion.nav initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2,
          ease: 'easeOut'
        }} className="fixed left-0 right-0 bg-white border-b border-grey-200 shadow-lg z-50 md:hidden" style={{
          top: headerRef.current?.getBoundingClientRect().bottom ?? 0
        }} data-id="element-46">
              <div className="px-6 py-4 flex flex-col gap-1" data-id="element-47">
                {navLinks.map((link, index) => <motion.div key={link.label} initial={{
              opacity: 0,
              x: -16
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.05,
              duration: 0.2
            }} data-id="element-48">
                    {link.isExternal ? link.isButton ? <a href={link.to} target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)} className="block w-full py-3 px-4 text-base font-semibold text-white bg-black hover:bg-grey-800 rounded-xl transition-colors text-center" data-id="element-49">
                          {link.label}
                        </a> : <a href={link.to} className="block py-3 px-4 text-base font-medium text-grey-700 hover:text-black hover:bg-grey-50 rounded-xl transition-colors" data-id="element-50">
                          {link.label}
                        </a> : <Link to={link.to} className="block py-3 px-4 text-base font-medium text-grey-700 hover:text-black hover:bg-grey-50 rounded-xl transition-colors" data-id="element-51">
                        {link.label}
                      </Link>}
                  </motion.div>)}
              </div>
            </motion.nav>
          </>}
      </AnimatePresence>

      {/* Coming Soon Lightbox */}
      <AnimatePresence data-id="element-52">
        {comingSoonOpen && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-id="element-53">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setComingSoonOpen(false)} data-id="element-54" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 25
        }} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md z-10 p-8 sm:p-10 text-center" onClick={e => e.stopPropagation()} data-id="element-55">
              <button onClick={() => setComingSoonOpen(false)} className="absolute top-4 right-4 p-2 rounded-full hover:bg-grey-100 transition-colors" aria-label="Close" data-id="element-56">
                <XIcon className="w-5 h-5 text-grey-500" data-id="element-57" />
              </button>

              <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-black" data-id="element-58">
                <RocketIcon className="h-6 w-6 text-white" data-id="element-59" />
              </div>

              <h2 className="text-2xl font-bold text-black mb-2" data-id="element-60">
                Coming Soon
              </h2>
              <p className="text-grey-500 text-sm leading-relaxed mb-6" data-id="element-61">
                We're working on making it easy for you to submit AI tools
                directly. This feature will be available soon!
              </p>

              <div className="space-y-3" data-id="element-62">
                <a href="mailto:tools@4all.ai?subject=Tool%20Submission" className="block w-full rounded-full bg-black text-white px-6 py-3 text-sm font-semibold hover:bg-grey-800 transition-colors" data-id="element-63">
                  Email us your tool
                </a>
                <button onClick={() => setComingSoonOpen(false)} className="block w-full rounded-full border border-grey-300 text-grey-700 px-6 py-3 text-sm font-medium hover:border-black hover:text-black transition-colors" data-id="element-64">
                  Close
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </header>;
}